/**
 * Utility to parse all markdown files in assets/content and update items in context
 * Automatically discovers markdown files and matches them to items
 */

import { parseMarkdownToDetailSection } from './markdownUtils';
import { dedupeCatalogItemsById } from './catalogDedupe';
import { apiFetchText } from './apiFetchPolicy';

/**
 * Comprehensive list of all markdown files organized by category
 * This is generated from scanning public/assets/content directory
 */
export const ALL_MARKDOWN_FILES = {
    academics: [
        'assets/content/academics/curriculum.md',
        'assets/content/academics/tutoring.md',
        'assets/content/academics/resources.md'
    ],
    coaching: [
        'assets/content/coaching/personal.md',
        'assets/content/coaching/sports.md',
        'assets/content/coaching/wellness.md'
    ],
    concierge: [
        'assets/content/concierge/assistant.md',
        'assets/content/concierge/guide.md'
    ],
    consulting: [
        'assets/content/consulting/consulting-business.md',
        'assets/content/consulting/hr.md',
        'assets/content/consulting/negotiation.md'
    ],
    crafts: [
        'assets/content/crafts/bag-business.md',
        'assets/content/crafts/bed.md',
        'assets/content/crafts/candle.md',
        'assets/content/crafts/carved-purse.md',
        'assets/content/crafts/chair-chinese.md',
        'assets/content/crafts/closet-chinese.md',
        'assets/content/crafts/crafts-beachchair.md',
        'assets/content/crafts/light-bag.md',
        'assets/content/crafts/oxford.md',
        'assets/content/crafts/pink-bag.md',
        'assets/content/crafts/purse.md',
        'assets/content/crafts/red-wing.md',
        'assets/content/crafts/row-boat.md',
        'assets/content/crafts/soap.md',
        'assets/content/crafts/storage-bench.md',
        'assets/content/crafts/table-chinese.md',
        'assets/content/crafts/wallet.md'
    ],
    gallery: [
        'assets/content/gallery/bikepacking.md',
        'assets/content/gallery/hiking.md',
        'assets/content/gallery/retreat.md',
        'assets/content/gallery/boating.md',
        'assets/content/gallery/boy.md',
        'assets/content/gallery/commission.md',
        'assets/content/gallery/studio.md',
        'assets/content/gallery/fenghuang-1.md',
        'assets/content/gallery/lake-park.md',
        'assets/content/gallery/marina.md',
        'assets/content/gallery/print.md',
        'assets/content/gallery/sail.md',
        'assets/content/gallery/sea.md',
        'assets/content/gallery/shangri-la.md',
        'assets/content/gallery/sunset.md',
        'assets/content/gallery/tibet-night.md',
        'assets/content/gallery/tibetan-lake.md',
        'assets/content/gallery/timescape.md',
        'assets/content/gallery/tshirt.md',
        'assets/content/gallery/xgll-1.md'
    ],
    hospitality: [
        'assets/content/hospitality/catering.md',
        'assets/content/hospitality/hospitality-event.md',
        'assets/content/hospitality/stay.md'
    ],
    publications: [
        'assets/content/publications/art-collection.md',
        'assets/content/publications/article-creativity.md',
        'assets/content/publications/bikepacking.md',
        'assets/content/publications/biography.md',
        'assets/content/publications/church.md',
        'assets/content/publications/commission.md',
        'assets/content/publications/content.md',
        'assets/content/publications/editorial.md',
        'assets/content/publications/eucharist-academy.md',
        'assets/content/publications/functional-nutrition.md',
        'assets/content/publications/history.md',
        'assets/content/publications/universality-for-learners.md',
        'assets/content/publications/universality-guide.md'
    ],
    software: [
        'assets/content/software/ai.md',
        'assets/content/software/data.md',
        'assets/content/software/mobile.md',
        'assets/content/software/quant.md',
        'assets/content/software/uiux.md',
        'assets/content/software/web.md'
    ]
};

/**
 * Extract filename from a markdown path
 * @param {string} path - Full path like "assets/content/academics/tutoring.md"
 * @returns {string} - Filename without extension, e.g., "tutoring"
 */
const getFilenameFromPath = (path) => {
    const parts = path.split('/');
    const filename = parts[parts.length - 1];
    return filename.replace('.md', '');
};

/**
 * Match an item to a markdown file
 * @param {Object} item - Item from context
 * @param {string} category - Category name
 * @param {Array} markdownFiles - Array of markdown file paths for the category
 * @returns {string|null} - Matched markdown file path or null
 */
const matchItemToMarkdown = (item, category, markdownFiles) => {
    // First, check if item has contentPath that matches a markdown file
    if (item.contentPath) {
        if (typeof item.contentPath === 'string') {
            // Normalize contentPath
            let normalizedPath = item.contentPath;
            if (!normalizedPath.startsWith('assets/content/')) {
                if (normalizedPath.includes('/')) {
                    normalizedPath = normalizedPath.replace(/^\/content\//, '').replace(/^content\//, '');
                    if (!normalizedPath.startsWith('assets/content/')) {
                        normalizedPath = `assets/content/${normalizedPath}`;
                    }
                } else {
                    normalizedPath = `assets/content/${category}/${normalizedPath}.md`;
                }
            }
            
            // Check if this path exists in markdown files
            if (markdownFiles.includes(normalizedPath)) {
                return normalizedPath;
            }
        }
    }
    
    // Second, try to match by shortTitle
    if (item.shortTitle) {
        const itemFilename = item.shortTitle.toLowerCase().replace(/\s+/g, '-');
        const matchingFile = markdownFiles.find(file => {
            const fileFilename = getFilenameFromPath(file).toLowerCase().replace(/\s+/g, '-');
            return fileFilename === itemFilename;
        });
        if (matchingFile) {
            return matchingFile;
        }
    }
    
    // Third, try to match by title (convert to filename format)
    if (item.title) {
        const itemFilename = item.title.toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '');
        const matchingFile = markdownFiles.find(file => {
            const fileFilename = getFilenameFromPath(file).toLowerCase().replace(/\s+/g, '-');
            return fileFilename === itemFilename || fileFilename.includes(itemFilename) || itemFilename.includes(fileFilename);
        });
        if (matchingFile) {
            return matchingFile;
        }
    }
    
    return null;
};

/**
 * Fetch markdown content from a path
 * @param {string} markdownPath - Path to markdown file
 * @returns {Promise<string|null>} - Markdown content or null if not found
 */
const fetchMarkdownContent = async (markdownPath) => {
    const base = process.env.PUBLIC_URL || '';
    const url = base ? `${base.replace(/\/$/, '')}/${markdownPath}` : `/${markdownPath}`;
    try {
        // Public static .md assets — route through unified client (timeout/retry);
        // non-2xx throws and is caught → null, preserving the prior contract.
        return await apiFetchText(url, { credentials: 'omit' });
    } catch (e) {}
    return null;
};

/**
 * Parse markdown for a single category. Used when lazy-loading categories.
 * @param {string} category - Category key
 * @param {Array} items - Items for the category
 * @param {Function} setItemsByCategory - State setter (updater function)
 * @param {Object} customMarkdownFiles - Optional (defaults to ALL_MARKDOWN_FILES)
 * @returns {Promise<Array>} - Updated items with detailSection
 */
export const parseMarkdownForCategory = async (category, items, setItemsByCategory, customMarkdownFiles = null) => {
  const markdownFiles = customMarkdownFiles || ALL_MARKDOWN_FILES;
  const categoryMarkdownFiles = markdownFiles[category] || [];
  if (!Array.isArray(items) || items.length === 0) return items;

  const updatedItems = await Promise.all(items.map(async (item) => {
    const matchedPath = matchItemToMarkdown(item, category, categoryMarkdownFiles);
    if (!matchedPath) return item;
    if (item.contentPath === matchedPath && item.detailSection) return item;

    const markdownContent = await fetchMarkdownContent(matchedPath);
    if (!markdownContent) return item;
    try {
      const detailSection = parseMarkdownToDetailSection(markdownContent);
      return { ...item, detailSection, contentPath: matchedPath };
    } catch {
      return item;
    }
  }));

  const deduped = dedupeCatalogItemsById(updatedItems);
  setItemsByCategory((prev) => ({ ...prev, [category]: deduped }));
  return deduped;
};
